import client from './client';

// ============================================================
// 项目部署（DW-P0-8）：策略/项目/发布/图片记录 —— 对话框与入口共用
// ============================================================

export type DeployTier = 'free' | 'basic' | 'pro' | 'enterprise';

export interface DeployArtifactPolicy {
  enabled: boolean;
  minTier: DeployTier;
  monthlyQuota: number;
  maxDays?: number;
  maxSizeMb?: number;
  maxQps?: number;
  maxDevices?: number;
  maxScale?: number;
  /** video：单次录制时长上限（秒，DW-P2-8） */
  maxDurationSec?: number;
  /** 客户端对话框是否露出该产物（DW-P1-12） */
  visible?: boolean;
  /** 产物保留天数（0=永久，DW-P1-12） */
  retentionDays?: number;
  allowed: boolean;
  used: number;
  remaining: number;
  requiredTier?: DeployTier;
  /** 当前套餐被运营显式停用（DW-P1-12） */
  deniedByTier?: boolean;
}

export interface DeployPolicyView {
  tier: DeployTier;
  artifacts: Record<string, DeployArtifactPolicy>;
  dist: { viewerMinVersion: string; sdkVersion: string };
  promo: { text: string; link: string };
}

export interface DeploymentRecord {
  id: string;
  name: string;
  type: 'scene' | 'dashboard' | 'fusion' | string;
  sceneId?: string | null;
  pageId?: string | null;
  projectCode: string;
  alias?: string | null;
  status: string;
  settings: Record<string, unknown>;
  oem: Record<string, unknown>;
  lastReleaseId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ReleaseRecord {
  id: string;
  version: number;
  kind: string;
  status: string;
  deployMode: string;
  options: Record<string, unknown>;
  manifest?: Record<string, unknown>;
  createdAt: string;
  job?: { id: string; status: string; progress: number } | null;
}

export interface CreateReleaseResponse {
  release: ReleaseRecord;
  token: { token: string } | null;
  job: { id: string; status: string; progress: number } | null;
  links: { short?: string; projectCode: string };
}

export const fetchDeployPolicy = () =>
  client.get('/deploy/policy').then((r) => r.data as DeployPolicyView);

export const listDeployments = (params?: { type?: string; sourceId?: string }) =>
  client.get('/deploy/deployments', { params }).then((r) => r.data as DeploymentRecord[]);

export const createDeployment = (body: {
  name?: string;
  type: string;
  sceneId?: string;
  pageId?: string;
}) => client.post('/deploy/deployments', body).then((r) => r.data as DeploymentRecord);

export const updateDeployment = (
  id: string,
  body: Partial<{ name: string; alias: string | null; settings: Record<string, unknown>; oem: Record<string, unknown> }>,
) => client.put(`/deploy/deployments/${id}`, body).then((r) => r.data as DeploymentRecord);

export const createRelease = (id: string, body: Record<string, unknown>) =>
  client.post(`/deploy/deployments/${id}/releases`, body).then((r) => r.data as CreateReleaseResponse);

export const listReleases = (id: string) =>
  client.get(`/deploy/deployments/${id}/releases`).then((r) => r.data as ReleaseRecord[]);

export const getRelease = (id: string) =>
  client.get(`/deploy/releases/${id}`).then((r) => r.data as ReleaseRecord);

export const unpublishDeployment = (id: string) =>
  client.post(`/deploy/deployments/${id}/unpublish`).then((r) => r.data as { ok: boolean; revokedTokens: number });

// ── DW-P1-4：部署激活（购买激活码/记录） ──

export interface DeployActivationRecord {
  id: string;
  deploymentId: string;
  userId: string;
  machineCode: string;
  features: string[];
  activationCode?: string | null;
  expiresAt?: string | null;
  revoked: boolean;
  activatedAt: string;
}

export interface IssueActivationResponse {
  activationCode: string;
  license: { deviceSn: string; expiresAt: number; validMonth: number; features: string[] };
  activation: DeployActivationRecord;
}

export const listActivations = (deploymentId: string) =>
  client.get(`/deploy/deployments/${deploymentId}/activations`).then((r) => r.data as DeployActivationRecord[]);

export const issueActivation = (deploymentId: string, body: { machineCode: string; extraFeatures?: string[] }) =>
  client.post(`/deploy/deployments/${deploymentId}/activations`, body).then((r) => r.data as IssueActivationResponse);

export const revokeActivation = (activationId: string) =>
  client.post(`/deploy/activations/${activationId}/revoke`).then((r) => r.data as { ok: boolean });

// ── DW-P1-6：OEM 素材（导航图标/加载 LOGO/登录背景） ──

export type OemSlot = 'navIcon' | 'logo' | 'loginBg';

export const uploadDeploymentOem = (deploymentId: string, slot: OemSlot, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return client
    .post(`/deploy/deployments/${deploymentId}/oem?slot=${slot}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((r) => r.data as DeploymentRecord);
};

// ── DW-P1-11：数据对接（只读数据 API + WS + 嵌入 SDK） ──

export interface DataCapabilities {
  rest: boolean;
  ws: boolean;
  sdk: boolean;
}

export interface DataTokenInfo {
  token: string | null;
  tokenId?: string;
  scopes?: string[];
  expose: { widgets?: string[]; devices?: string[]; points?: string[]; origins?: string[]; qps?: number };
  capabilities: DataCapabilities;
}

export const getDataToken = (deploymentId: string) =>
  client.get(`/deploy/deployments/${deploymentId}/data-token`).then((r) => r.data as DataTokenInfo);

export const createDataToken = (deploymentId: string, rotate = false) =>
  client
    .post(`/deploy/deployments/${deploymentId}/data-token`, { rotate })
    .then((r) => r.data as DataTokenInfo & { token: string; tokenId: string });

export const revokeDataToken = (deploymentId: string) =>
  client.delete(`/deploy/deployments/${deploymentId}/data-token`).then((r) => r.data as { ok: boolean; revoked: number });

export const putDataScope = (
  deploymentId: string,
  scope: { widgets?: string[]; devices?: string[]; points?: string[]; origins?: string[]; qps?: number },
) =>
  client
    .put(`/deploy/deployments/${deploymentId}/data-scope`, scope)
    .then((r) => r.data as { ok: boolean; expose: DataTokenInfo['expose'] });

// ── DW-P1-10：设备接入工程包 ──

export interface IotPackageView {
  meta: Record<string, unknown>;
  deviceBindings: Record<string, unknown>[];
  points: Record<string, string>[];
  protocolMap: Record<string, unknown>;
}

export interface IotValidateReport {
  ok: boolean;
  errors: { code: string; message: string; index?: number }[];
  warnings: { code: string; message: string; index?: number }[];
  stats: { bindings: number; points: number; devices: number };
}

export const getIotPackage = (deploymentId: string) =>
  client.get(`/deploy/deployments/${deploymentId}/iot-package`).then((r) => r.data as IotPackageView);

export const exportIotPackage = (deploymentId: string) =>
  client
    .post(`/deploy/deployments/${deploymentId}/iot-package/export`)
    .then((r) => r.data as { releaseId: string; version: number; status: string; package: { fileName: string; size: number } });

export const validateIotPackage = (body: {
  deviceBindings: Record<string, unknown>[];
  points: Record<string, string>[];
  protocolMap?: Record<string, unknown>;
}) => client.post('/deploy/iot-package/validate', body).then((r) => r.data as IotValidateReport);

// ── DW-P1-9：边缘下发（kind='project' → edge-upgrade） ──

export interface DeliverRecord {
  id: string;
  createdAt?: string;
  deviceSns?: string[];
  upgradeId?: string;
  gray?: number;
  force?: boolean;
}

// ---------- DW-P2-10：边缘 AI 模型包 ----------

export interface ModelFileInfo {
  name: string;
  size: number;
  mtime: number;
  format: string;
}

export const listModelFiles = () =>
  client.get('/deploy/model-package/models').then((r) => (r.data?.items ?? []) as ModelFileInfo[]);

export const uploadModelFile = (file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return client
    .post('/deploy/model-package/upload', fd)
    .then((r) => r.data as { modelName: string; size: number; sha256: string; format: string });
};

export const deliverModelPackage = (releaseId: string, deviceSns: string[]) =>
  client
    .post(`/deploy/releases/${releaseId}/deliver`, { deviceSns })
    .then((r) => r.data as {
      ok: boolean;
      total: number;
      queued: number;
      failed: number;
      results: { imei: string; ok: boolean; error?: string; bytes?: number }[];
    });

export const deliverRelease = (
  releaseId: string,
  body: { deviceSns: string[]; gray?: number; force?: boolean; note?: string },
) =>
  client
    .post(`/deploy/releases/${releaseId}/deliver`, body)
    .then((r) => r.data as { ok: boolean; upgradeId: string; version: string; devices: string[]; fileUrl: string });

export const listDeliveries = (releaseId: string) =>
  client.get(`/deploy/releases/${releaseId}/deliveries`).then((r) => r.data as DeliverRecord[]);

/** DW-P1-7：设置/清除访问密码（空=公开访问） */
export const setDeploymentLinkPassword = (deploymentId: string, password: string | null) =>
  client
    .put(`/deploy/deployments/${deploymentId}/link-password`, { password })
    .then((r) => r.data as { ok: boolean; passwordRequired: boolean });

export const removeDeploymentOem = (deploymentId: string, slot: OemSlot) =>
  client.delete(`/deploy/deployments/${deploymentId}/oem/${slot}`).then((r) => r.data as DeploymentRecord);

/** 下载产物（zip/.dtwpkg，带鉴权 blob → 触发浏览器下载） */
export async function downloadReleaseFile(releaseId: string, fileName: string): Promise<void> {
  const res = await client.get(`/deploy/releases/${releaseId}/download`, { responseType: 'blob' });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** 解析部署接口错误（403/429 带 code，用于套餐引导与文案） */
export function deployErrorOf(err: unknown): {
  code: string;
  message: string;
  requiredTier?: DeployTier;
  /** 别名冲突时服务端给出的可用建议（DW：P2 反馈修复） */
  suggestion?: string;
} {
  const res = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
  const data = res?.data ?? {};
  return {
    code: String(data.code ?? `HTTP_${res?.status ?? 'ERR'}`),
    message: String(data.message ?? '部署失败，请稍后重试'),
    requiredTier: data.requiredTier as DeployTier | undefined,
    suggestion: typeof data.suggestion === 'string' ? data.suggestion : undefined,
  };
}

/** 查看器安装器（desktop-upgrade kind=app，公开 manifest） */
export interface ViewerInstallerInfo {
  version: string;
  fileUrl: string;
  size: number;
  sha512?: string;
}

export const fetchViewerInstaller = (): Promise<ViewerInstallerInfo | null> =>
  client
    .get('/desktop/manifest', { params: { platform: 'win' } })
    .then((r) => {
      const files = (r.data?.data?.files ?? r.data?.files ?? []) as Array<Record<string, unknown>>;
      const app = files.find((f) => f.kind === 'app');
      if (!app) return null;
      return {
        version: String(app.version ?? ''),
        fileUrl: String(app.fileUrl ?? ''),
        size: Number(app.size ?? 0),
        sha512: app.sha512 ? String(app.sha512) : undefined,
      };
    });

/** U 盘离线包构件预检（installer/package/edge/iot/model/sdk/rtuTool 可用性） */
export interface UsbPreflightItem {
  key: string;
  label: string;
  available: boolean;
  detail?: string;
}

export const fetchUsbPreflight = (deploymentId?: string) =>
  client
    .get('/deploy/usb-package/preflight', { params: deploymentId ? { deploymentId } : {} })
    .then((r) => (r.data?.items ?? []) as UsbPreflightItem[]);
